import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  FileText, 
  CheckCircle, 
  BarChart3, 
  Users, 
  Target, 
  Search, 
  ClipboardCheck, 
  TrendingUp, 
  ShieldCheck, 
  Calendar, 
  PieChart, 
  Settings, 
  X,
  FileCheck,
  Briefcase
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ContactForm from '../ContactForm';
import ParticlesBackground from '../components/ParticlesBackground';
import LanguageSwitcher from '../components/LanguageSwitcher';

// Header Component
const PageHeader = ({ onOpenPopup, t }) => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
      <a href="/" className="flex items-center">
        <img
          src="https://imgur.com/nYWIvWJ.png"
          alt="Nextcoders Logo"
          className="h-8"
          onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders"; }}
        />
      </a>
      <div className="flex items-center gap-4 md:gap-8">
        <LanguageSwitcher variant="compact" />
        <button
          onClick={onOpenPopup}
          className="bg-[#141414] text-white px-4 md:px-6 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-semibold"
        >
          {t('dap.header_cta')}
        </button>
      </div>
    </div>
  </header>
);

const DapPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const heroImageUrl = '/images/dap/dap.png';

  const openPopup = () => {
    setIsPopupOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    document.body.style.overflow = 'auto';
  };

  // Componente para tarjetas de objetivos
  const ObjectiveCard = ({ icon: Icon, title, description, colorClass }) => (
    <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300">
      <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${colorClass.bg} ${colorClass.text}`}>
        <Icon size={24} />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  );

  // Componente para pasos del proceso
  const ProcessStep = ({ number, title, description, icon: Icon }) => (
    <div className="flex flex-col items-center text-center p-4">
      <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mb-4 shadow-lg relative">
        {number}
        {Icon && <Icon size={16} className="absolute -top-1 -right-1 bg-green-500 rounded-full p-0.5" />}
      </div>
      <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-500 text-sm">{description}</p>
    </div>
  );

  const DiagramStep = ({ number, icon: Icon, title, description }) => (
    <div className="flex items-start gap-4">
      <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-blue-600 text-white flex items-center justify-center font-extrabold shadow-sm">
        {number}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-blue-700" />
          <div className="font-bold text-gray-900">{title}</div>
        </div>
        <div className="text-sm text-gray-600 mt-1 leading-relaxed">{description}</div>
      </div>
    </div>
  );

  const DeliverableCard = ({ icon: Icon, title, description, colorClass }) => (
    <div className="bg-white border border-gray-200 rounded-2xl p-5">
      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-3 ${colorClass}`}>
        <Icon size={20} />
      </div>
      <div className="font-bold text-gray-900">{title}</div>
      <div className="text-sm text-gray-600 mt-1 leading-relaxed">{description}</div>
    </div>
  );

  const objectiveItems = useMemo(() => {
    const items = t('dap.objectives.items', { returnObjects: true });
    const keys = ['identification', 'feasibility', 'decisions', 'planning'];
    const icons = [Search, BarChart3, Target, TrendingUp];
    const colors = [
      { bg: 'bg-blue-100', text: 'text-blue-600' },
      { bg: 'bg-green-100', text: 'text-green-600' },
      { bg: 'bg-purple-100', text: 'text-purple-600' },
      { bg: 'bg-orange-100', text: 'text-orange-600' },
    ];
    return keys.map((key, i) => ({ ...items[key], icon: icons[i], colorClass: colors[i] }));
  }, [t]);

  const components = useMemo(() => t('dap.components.items', { returnObjects: true }), [t]);
  const developmentSteps = useMemo(() => t('dap.development.steps', { returnObjects: true }), [t]);
  const analysisSteps = useMemo(() => t('dap.analysis.sequence', { returnObjects: true }), [t]);
  const analysisDeliverables = useMemo(() => t('dap.analysis.deliverables', { returnObjects: true }), [t]);
  const successKeys = useMemo(() => t('dap.success_keys.items', { returnObjects: true }), [t]);

  const componentIcons = [Target, Target, BarChart3, ClipboardCheck, ShieldCheck, Briefcase];
  const componentIconColors = ['text-red-600', 'text-blue-700', 'text-indigo-700', 'text-purple-600', 'text-green-600', 'text-orange-600'];
  const diagramIcons = [Users, Search, Target, BarChart3, ClipboardCheck, ShieldCheck, Briefcase, FileCheck];
  const deliverableIcons = [Search, Target, BarChart3, ClipboardCheck, FileCheck, PieChart];
  const deliverableColors = ['bg-blue-100 text-blue-700', 'bg-indigo-100 text-indigo-700', 'bg-emerald-100 text-emerald-700', 'bg-purple-100 text-purple-700', 'bg-gray-200 text-gray-800', 'bg-teal-100 text-teal-700'];
  const successIcons = [Users, Search, Settings, CheckCircle, TrendingUp, Calendar];
  const successCardColors = ['bg-blue-50', 'bg-green-50', 'bg-purple-50', 'bg-orange-50', 'bg-teal-50', 'bg-indigo-50'];
  const successIconColors = ['text-blue-600', 'text-green-600', 'text-purple-600', 'text-orange-600', 'text-teal-600', 'text-indigo-600'];

  return (
    <div className="relative min-h-screen bg-transparent font-sans text-gray-800 selection:bg-blue-100">
      <ParticlesBackground />
      
      {/* Popup Modal */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) closePopup(); }}>
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative transform transition-all duration-300 scale-100">
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition"
              aria-label={t('dap.popup.close')}
            >
              <X size={24} />
            </button>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#141414] mb-3">{t('dap.popup.title')}</h3>
              <p className="text-gray-600 mb-6">{t('dap.popup.desc')}</p>
              <ContactForm
                formData="inline/4/10oz6w"
                formId="4"
                scriptSrc="https://cdn.bitrix24.es/b24284919/crm/form/loader_4.js"
              />
            </div>
          </div>
        </div>
      )}

      <PageHeader onOpenPopup={openPopup} t={t} />

      <main className="pb-20">
        <section className="relative w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] mb-20">
          <div className="relative overflow-hidden bg-[#0B0B0B] min-h-screen">
            <div className="absolute top-0 left-0 right-0 z-20">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-24">
                <button
                  type="button"
                  onClick={() => {
                    if (window.history.length > 1) navigate(-1);
                    else navigate('/');
                  }}
                  className="inline-flex items-center gap-2 text-white/90 hover:text-white transition font-semibold bg-white/10 border border-white/15 backdrop-blur px-4 py-2 rounded-full"
                >
                  <ArrowLeft size={16} />
                  {t('dap.back')}
                </button>
              </div>
            </div>

            <div className="absolute inset-0">
              <img
                src={heroImageUrl}
                alt={t('dap.hero_alt')}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/2400x1600/0b0b0b/ffffff?text=Portada+DAP'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/35 to-black/10" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>

            <div className="relative z-10 p-8 md:p-14 min-h-screen flex flex-col justify-end max-w-7xl mx-auto">
              <div className="inline-flex items-center gap-3 bg-white/10 text-white px-4 py-2 rounded-full w-fit mb-6 backdrop-blur border border-white/15">
                <FileText size={18} />
                <span className="text-sm font-semibold">{t('dap.badge')}</span>
              </div>

              <h1 className="text-4xl md:text-6xl font-extrabold text-white leading-[1.05] tracking-tight max-w-5xl">
                {t('dap.title')}
              </h1>

              <p className="mt-6 text-xl md:text-2xl text-white/90 max-w-4xl font-medium">
                {t('dap.subtitle')}
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={openPopup}
                  className="bg-white text-[#141414] hover:bg-gray-100 font-bold py-3 px-8 rounded-full transition"
                >
                  {t('dap.cta_advisory')}
                </button>
                <a
                  href="#que-es"
                  className="bg-transparent border border-white/40 text-white hover:bg-white/10 font-bold py-3 px-8 rounded-full transition"
                >
                  {t('dap.cta_content')}
                </a>
              </div>
            </div>
          </div>
        </section>

        <div className="px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <section id="que-es" className="mb-24 bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-full -mr-32 -mt-32 opacity-50"></div>
              <div className="relative z-10">
                <h2 className="text-3xl font-bold text-[#141414] mb-6">{t('dap.what_is.title')}</h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  {t('dap.what_is.desc')}
                </p>
                <blockquote className="border-l-4 border-blue-600 pl-6 italic text-xl text-gray-700 bg-gray-50 p-6 rounded-r-xl">
                  {t('dap.what_is.quote')}
                </blockquote>
              </div>
            </section>

          {/* Objetivos */}
          <section className="mb-24">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#141414]">{t('dap.objectives.title')}</h2>
              <p className="text-gray-500 mt-2">{t('dap.objectives.subtitle')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {objectiveItems.map((item, i) => (
                <ObjectiveCard key={i} icon={item.icon} title={item.title} description={item.desc} colorClass={item.colorClass} />
              ))}
            </div>
          </section>

          {/* Componentes Esenciales */}
          <section className="mb-24">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-12">{t('dap.components.title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {components.map((comp, i) => {
                const Icon = componentIcons[i] || Target;
                return (
                  <div key={comp.title || i} className="bg-gray-50 p-6 rounded-xl border border-gray-200 flex gap-4">
                    <Icon className={`${componentIconColors[i] || 'text-blue-700'} flex-shrink-0`} size={32} />
                    <div>
                      <h3 className="font-bold text-lg mb-2">{comp.title}</h3>
                      <p className="text-gray-600 text-sm">{comp.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Proceso de Desarrollo */}
          <section className="mb-24 bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-3xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-12">{t('dap.development.title')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {developmentSteps.map((step, i) => (
                <ProcessStep key={step.title || i} number={String(i + 1)} title={step.title} description={step.desc} />
              ))}
            </div>
          </section>

          <section className="mb-24 bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-[#141414] mb-4">{t('dap.analysis.title')}</h2>
              <p className="text-gray-600 max-w-4xl mx-auto">
                {t('dap.analysis.desc')}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
              <div className="bg-gray-50 border border-gray-200 rounded-3xl p-8 h-full flex flex-col">
                <h3 className="text-xl font-bold text-gray-900 mb-6">{t('dap.analysis.sequence_title')}</h3>
                <div className="space-y-6">
                  {analysisSteps.map((step, i) => (
                    <DiagramStep key={step.title || i} number={String(i + 1)} icon={diagramIcons[i] || FileCheck} title={step.title} description={step.desc} />
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-3xl p-8 h-full flex flex-col">
                <h3 className="text-xl font-bold text-gray-900 mb-6">{t('dap.analysis.deliverables_title')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {analysisDeliverables.slice(0, 6).map((item, i) => (
                    <DeliverableCard
                      key={item.title || i}
                      icon={deliverableIcons[i] || FileCheck}
                      title={item.title}
                      description={item.desc}
                      colorClass={deliverableColors[i] || 'bg-blue-100 text-blue-700'}
                    />
                  ))}
                </div>
                <div className="mt-6 bg-white border border-gray-200 rounded-2xl p-5">
                  <div className="flex items-start gap-4">
                    <Calendar className="text-indigo-700 flex-shrink-0" size={24} />
                    <div className="min-w-0">
                      <div className="font-bold text-gray-900">{analysisDeliverables?.[6]?.title}</div>
                      <div className="text-sm text-gray-600 mt-1 leading-relaxed">
                        {analysisDeliverables?.[6]?.desc}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Consideraciones Clave */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-12">{t('dap.success_keys.title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {successKeys.map((item, i) => {
                const Icon = successIcons[i] || Settings;
                return (
                  <div key={item.title || i} className={`${successCardColors[i] || 'bg-gray-50'} p-6 rounded-xl text-center`}>
                    <Icon className={`mx-auto ${successIconColors[i] || 'text-gray-600'} mb-4`} size={32} />
                    <h3 className="font-bold mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Conclusión y CTA */}
          <div className="bg-gray-900 rounded-3xl p-12 text-center text-white relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full opacity-10">
              <ParticlesBackground color="#ffffff" density={30} />
            </div>
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-6">{t('dap.final_cta.title')}</h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8 leading-relaxed">
                {t('dap.final_cta.desc')}
              </p>
              <button
                onClick={openPopup}
                className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-4 px-10 rounded-full transition transform hover:scale-105 shadow-lg"
              >
                {t('dap.final_cta.button')}
              </button>
            </div>
          </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DapPage;

